import { el, clear } from '../../core/dom.js';
import { api } from '../../core/api.js';
import { errorPanel } from '../../components/error.js';
import { button } from '../../components/button.js';
import { skeleton } from '../../components/skeleton.js';
import { toast } from '../../components/toast.js';
import { listUsers, publicSupport, supportSession } from './actions.js';
import {
  billingSummary,
  mailtoSupport,
  planLabel,
  recentSupportMessages,
  sessionWhen,
  supportMessageCheck,
} from './billing-model.js';

/**
 * @param {{
 *   host: HTMLElement,
 *   me: object,
 *   section: 'billing'|'support',
 *   productId?: string,
 *   dead: () => boolean,
 * }} ctx
 */
export function mountBillingSupport(ctx) {
  const state = {
    loading: true,
    fatal: '',
    bookingUrl: '',
    supportEmail: '',
    session: null,
    userCount: null,
    messages: [],
    sending: false,
  };

  function paint() {
    if (ctx.dead()) return;
    clear(ctx.host);
    if (state.loading) {
      ctx.host.append(skeleton(4));
      return;
    }
    if (state.fatal) {
      ctx.host.append(errorPanel({
        title: ctx.section === 'support' ? 'Support unavailable' : 'Billing unavailable',
        message: state.fatal,
        action: button('Try again', { variant: 'primary', onClick: () => load(true) }),
      }));
      return;
    }
    if (ctx.section === 'support') ctx.host.append(supportPanel(state, ctx));
    else ctx.host.append(billingPanel(ctx.me, state));
  }

  async function load(initial) {
    if (initial) {
      state.loading = true;
      state.fatal = '';
      paint();
    }
    const jobs = [publicSupport()];
    if (ctx.section === 'support') {
      jobs.push(supportSession(), api('/support/messages'));
    } else {
      jobs.push(listUsers());
    }
    const [pub, second, third] = await Promise.all(jobs);
    if (ctx.dead()) return;
    state.loading = false;
    state.fatal = '';
    state.bookingUrl = (pub && !pub.error && pub.booking_url) || '';
    state.supportEmail = (pub && !pub.error && pub.support_email) || 'hello@bonnieos.com';
    if (ctx.section === 'support') {
      state.session = second && !second.error ? second.session : null;
      state.messages = (third && !third.error && third.messages) || [];
    } else {
      const users = (second && !second.error && second.users) || [];
      state.userCount = users.length;
    }
    paint();
  }

  paint();
  load(true);
  return { refresh: () => load(false), dispose() {} };
}

function billingPanel(me, state) {
  const summary = billingSummary(me, { userCount: state.userCount });
  return el('div', { class: 'set-stack' },
    el('div', { class: 'set-panel' },
      el('div', { class: 'set-panel-head' },
        el('div', { class: 'set-panel-title' },
          el('span', {}, 'Current plan'),
          el('span', { class: 'set-chip' }, summary.plan))),
      el('div', { class: 'set-byok-body' },
        el('p', {},
          'Self-serve billing is not live yet. Your workspace is on the ',
          el('strong', {}, planLabel(me)),
          ' plan during beta.'),
        el('div', { class: 'set-model-stats bil-stats' },
          summary.lines.map((line) => el('div', { class: 'set-stat' },
            el('span', { class: 'lbl' }, line.label),
            el('span', {}, line.value)))),
        el('p', { class: 'set-hint' },
          'Seat count comes from Team & access. Included AI usage and invoices will appear here when plans ship.'))),
    el('div', { class: 'set-link-grid' },
      linkCard({
        title: 'Talk to sales',
        copy: 'Questions about plans, seats, or invoices - reach the Bonnie team.',
        href: mailtoSupport(state.supportEmail),
        label: `Email ${state.supportEmail || 'support'}`,
      }),
      linkCard({
        title: 'AI models & keys',
        copy: 'Bring-your-own provider keys live under AI models until included usage ships.',
        href: '/app/settings/models',
        label: 'Open AI models',
        internal: true,
      }),
      linkCard({
        title: 'Tenant support',
        copy: 'Message the team or book a live setup session from Support.',
        href: '/app/settings/support',
        label: 'Open support',
        internal: true,
      })));
}

function supportPanel(state, ctx) {
  const when = sessionWhen(state.session);
  const recent = recentSupportMessages(state.messages, 8);
  return el('div', { class: 'set-stack' },
    el('div', { class: 'set-link-grid' },
      linkCard({
        title: 'Chat with the team',
        copy: 'Ask a question or report an issue. Usually a reply within a few hours.',
        href: mailtoSupport(state.supportEmail),
        label: `Email ${state.supportEmail || 'support'}`,
      }),
      linkCard({
        title: 'Talk it through live',
        copy: 'Pick a time to walk through onboarding or setup with someone on the team.',
        href: state.bookingUrl || null,
        label: state.bookingUrl ? 'Choose a time' : 'Booking link not configured',
        disabled: !state.bookingUrl,
      })),
    when
      ? el('div', { class: 'set-note' },
        el('span', { class: 'set-note-kicker' }, 'Session booked'),
        el('p', {}, `${when} · With the Bonnie team`))
      : null,
    messagePanel(state, ctx),
    el('div', { class: 'set-note' },
      el('span', { class: 'set-note-kicker' }, 'Response times'),
      el('p', {},
        'Chat: within a few hours, Mon–Fri. Sessions: next available slot shown when you book - usually within 2 business days.')));
}

function messagePanel(state, ctx) {
  const form = { text: '' };
  const area = el('textarea', {
    class: 'set-input bil-area',
    rows: '4',
    maxlength: '4000',
    placeholder: 'What are you stuck on? Do not paste keys or tokens.',
    onInput: (event) => { form.text = event.target.value; },
  });
  const send = el('button', {
    type: 'button',
    class: 'set-btn set-btn-primary',
    onClick: async () => {
      const check = supportMessageCheck(form.text);
      if (!check.ok) {
        toast(check.error, 'error');
        return;
      }
      send.disabled = true;
      send.textContent = 'Sending…';
      let user = {};
      try { user = JSON.parse(localStorage.getItem('user') || '{}') || {}; } catch { user = {}; }
      const result = await api('/support/message', {
        method: 'POST',
        body: {
          message: check.message,
          tenant: user.tenant_name || user.tenant_slug || '',
          product: ctx.productId || '',
        },
      });
      send.disabled = false;
      send.textContent = 'Send message';
      if (ctx.dead()) return;
      if (!result || result.error) {
        toast((result && result.error) || 'Could not send the message', 'error');
        return;
      }
      form.text = '';
      area.value = '';
      toast('Message sent');
      const refreshed = await api('/support/messages');
      if (ctx.dead()) return;
      state.messages = (refreshed && !refreshed.error && refreshed.messages) || state.messages;
      // Re-paint only the message panel host by reloading the section.
      clear(ctx.host);
      ctx.host.append(supportPanel(state, ctx));
    },
  }, 'Send message');

  const thread = recentSupportMessages(state.messages, 8);
  return el('div', { class: 'set-panel' },
    el('div', { class: 'set-panel-head' },
      el('div', { class: 'set-panel-title' }, el('span', {}, 'In-app messages'))),
    el('div', { class: 'set-byok-body' },
      thread.length
        ? el('ul', { class: 'bil-thread' }, thread.map((msg) => el('li', {
          class: msg.role === 'admin' ? 'bil-msg is-admin' : 'bil-msg',
        },
        el('span', { class: 'lbl' }, msg.role === 'admin' ? 'CraftisanLabs' : 'You'),
        el('p', {}, msg.text || ''))))
        : el('p', { class: 'set-hint' }, 'No messages yet - send one below.'),
      el('p', { class: 'set-hint' }, 'Never paste keys in chat. Credentials stay on the connector form.'),
      area,
      el('div', { class: 'bil-send' }, send)));
}

function linkCard(opts) {
  const action = opts.href && !opts.disabled
    ? el('a', {
      class: 'set-btn set-btn-primary',
      href: opts.href,
      target: opts.internal ? null : '_blank',
      rel: opts.internal ? null : 'noopener noreferrer',
    }, opts.label)
    : el('span', { class: 'set-hint' }, opts.label);
  return el('div', { class: 'set-link-card' },
    el('h2', {}, opts.title),
    el('p', {}, opts.copy),
    action);
}
